/**
 * Dynamically load and display a list of projects
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {Spring} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {LabelSmall} from "@khanacademy/wonder-blocks-typography";

import type {ProgramSimple} from "#scratchpad-shared-package/types.ts";

import type {ProjectListFetcher} from "./project-list-fetchers/project-list-fetcher-types.ts";
import {normalizeSortOrder} from "./project-list-fetchers/sort-order.ts";
import ProjectList from "./project-list.tsx";

type Props = {
    // The default sort to use
    defaultSort: string;
    // Should the bar listing sorting types be shown
    hideFilterBar?: boolean;
    // Should the notice about which projects are shown
    // be displayed
    hideNotice?: boolean;
    // A function that renders an explanation if there are no
    // results found.
    noResults: () => React.ReactElement<any> | null | undefined;
    // A possible selection of sorts to choose from
    sorts: Array<string>;
    // Set to true to disallow dynamically loading more matches
    static?: boolean;
    // Set to true to show a dropdown for project status filtering
    // Helpful for teachers to find which projects need evaluation
    showProjectStatusFilter?: boolean;
    // Title of the project list to display at the top
    title?: string;
    // Called when the component has loaded at least one page of projects
    onFullyInteractive?: () => Promise<void>;
    // Used to fetch projects from the server.
    projectListFetcher: ProjectListFetcher;
};

type State = {
    complete: boolean;
    cursor: string;
    projects: ReadonlyArray<ProgramSimple> | null | undefined;
    sort: string;
    projectStatus: null | undefined | "all" | "official-spinoffs";
    loading: boolean;
    error: string | null | undefined;
};

class DynamicProjectList extends React.Component<Props, State> {
    _isMounted: boolean;

    static defaultProps: {
        defaultSort: string;
        noResults: () => null;
        sorts: Array<string>;
    } = {
        defaultSort: "top",
        noResults: () => null,
        sorts: ["top", "recent"],
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            loading: false,
            complete: false,
            cursor: "",
            error: null,
            projects: null,
            projectStatus: null,
            sort: props.defaultSort,
        };

        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;
        this.clearAndLoadProjects();
    }

    componentDidUpdate(prevProps: Props) {
        if (
            prevProps.projectListFetcher.fingerprint() !==
            this.props.projectListFetcher.fingerprint()
        ) {
            this.clearAndLoadProjects();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    clearAndLoadProjects() {
        this.setState({projects: null});
        this.loadMoreProjects();
    }

    loadMoreProjects: () => void = () => {
        const {onFullyInteractive} = this.props;

        if (this.state.loading) {
            return;
        }

        if (this._isMounted) {
            this.setState({error: null, loading: true});
        }

        this.props.projectListFetcher
            .fetch({
                sort: normalizeSortOrder(this.state.sort),
                cursor: this.state.cursor,
                filter: this.state.projectStatus ?? undefined,
            })
            .then((results) => {
                const projects = (this.state.projects || []).concat(
                    results.scratchpads,
                );
                if (this._isMounted) {
                    this.setState({
                        projects,
                        complete: results.complete,
                        cursor: results.cursor,
                        loading: false,
                    });
                }

                if (onFullyInteractive) {
                    onFullyInteractive();
                }
            })
            .catch(() => {
                if (this._isMounted) {
                    this.setState({
                        error: t`Error loading projects, please try again.`,
                        loading: false,
                    });
                }
            });
    };

    changeProjectStatus: (projectStatus: string) => void = (
        projectStatus: string,
    ) => {
        if (projectStatus !== "all" && projectStatus !== "official-spinoffs") {
            return;
        }
        if (this._isMounted) {
            this.setState(
                {
                    complete: false,
                    cursor: "",
                    projects: null,
                    projectStatus,
                },
                () => this.loadMoreProjects(),
            );
        }
    };

    renderProjectStatusFilter: () => React.ReactNode = () => {
        if (!this.props.showProjectStatusFilter) {
            return null;
        }

        return (
            <View>
                <SingleSelect
                    aria-label={t`Project status`}
                    onChange={this.changeProjectStatus}
                    placeholder="Filter by project status"
                    selectedValue={this.state.projectStatus || "all"}
                    style={styles.dropdown}
                >
                    <OptionItem label={t`All projects`} value="all" />
                    <OptionItem
                        label={t({
                            context:
                                'here, "programs" refers to computer programs.',
                            message: "Only spin-offs of official programs",
                        })}
                        value="official-spinoffs"
                    />
                </SingleSelect>
            </View>
        );
    };

    changeSort: (sort: string) => void = (sort: string) => {
        if (this._isMounted) {
            this.setState(
                {
                    complete: false,
                    cursor: "",
                    projects: null,
                    sort,
                },
                () => this.loadMoreProjects(),
            );
        }
    };

    renderSorts: () => React.ReactNode = () => {
        if (this.props.sorts.length === 0) {
            return null;
        }

        // Possible sorts
        const sorts: Record<
            string,
            {
                name: string;
                value: number;
            }
        > = {
            top: {
                name: t`Top`,
                value: 1, // SORT_TOP
            },

            recent: {
                name: t`Recent`,
                value: 2, // SORT_RECENT
            },

            hot: {
                name: t`Hot`,
                value: 3, // SORT_HOT
            },

            spotlight: {
                name: t`Contests`,
                value: 4, // SORT_OFFICIAL
            },

            votes: {
                name: t`Votes`,
                value: 5, // SORT_UPVOTE
            },

            oldest: {
                name: t`Oldest`,
                value: 6, // SORT_OLDEST
            },
        };

        return (
            <View style={styles.sorts}>
                {this.props.sorts.map((sortName) => (
                    <Button
                        kind="tertiary"
                        onClick={() => this.changeSort(sortName)}
                        disabled={
                            this.state.loading || sortName === this.state.sort
                        }
                        key={sorts[sortName].value}
                        style={styles.sortButton}
                    >
                        {sorts[sortName].name}
                    </Button>
                ))}
            </View>
        );
    };

    renderFilterBar: () => React.ReactNode = () => {
        if (this.props.hideFilterBar) {
            return null;
        }

        return (
            <View style={styles.filterBar}>
                {this.renderTitle()}
                {this.renderProjectStatusFilter()}
                <Spring />
                {this.renderSorts()}
            </View>
        );
    };

    renderNotice: () => React.ReactNode = () => {
        if (this.props.hideNotice) {
            return null;
        }

        return (
            <View style={styles.noticeWrap}>
                <LabelSmall style={styles.notice}>
                    {t`Note: we don't show projects that have no changes, are by child accounts, or are by anonymous users.`}
                </LabelSmall>
            </View>
        );
    };

    renderError: () => null | React.ReactElement<React.ComponentProps<"p">> =
        () => {
            if (!this.state.error) {
                return null;
            }

            return <p className={css(styles.error)}>{this.state.error}</p>;
        };

    renderShowMoreButton: () => React.ReactNode = () => {
        if (this.props.static || this.state.complete) {
            return null;
        }

        return (
            <View style={styles.showMore}>
                <Button
                    onClick={() => this.loadMoreProjects()}
                    disabled={this.state.loading}
                    kind="secondary"
                >
                    {t`Show more projects`}
                </Button>
                {this.renderError()}
            </View>
        );
    };

    renderTitle: () => null | React.ReactElement<React.ComponentProps<"h3">> =
        () => {
            if (!this.props.title) {
                return null;
            }

            return <h3 className={css(styles.title)}>{this.props.title}</h3>;
        };

    render(): React.ReactNode {
        const projects = this.state.projects;
        if (!projects) {
            return (
                <View>
                    {this.renderFilterBar()}
                    {this.renderError()}
                    {!this.state.error && <CircularSpinner />}
                </View>
            );
        }

        if (projects.length === 0) {
            return (
                <View>
                    {this.renderFilterBar()}
                    {this.props.noResults()}
                </View>
            );
        }

        return (
            <View>
                {this.renderFilterBar()}
                <ProjectList projects={projects} />
                {this.state.loading && <CircularSpinner />}
                {this.renderError()}
                {this.renderNotice()}
                {this.renderShowMoreButton()}
            </View>
        );
    }
}

const styles = StyleSheet.create({
    title: {
        lineHeight: "37px",
        marginBottom: 0,
        verticalAlign: "bottom",
    },

    dropdown: {
        width: 350,
    },

    filterBar: {
        flexDirection: "row",
        borderBottom: `1px solid ${color.offBlack16}`,
        marginBottom: 22,
        paddingBottom: 10,
    },

    sorts: {
        flexDirection: "row",
    },

    sortButton: {
        marginLeft: 10,
    },

    error: {
        color: color.red,
    },

    noticeWrap: {
        color: color.offBlack64,
        margin: "22px 0",
        textAlign: "center",
    },

    notice: {
        margin: "0 auto",
        maxWidth: 450,
        textAlign: "left",
    },

    showMore: {
        alignItems: "center",
        borderTop: `1px solid ${color.offBlack16}`,
        marginTop: 22,
        paddingTop: 12,
    },
});

export default DynamicProjectList;
